import { type ActivePodcastOrStation, StreamType } from '@4tn/core-audio-player-shared';
import Cookies from 'js-cookie';
import { JWPLAYER_SDK } from './consts';
import { type PlayerConfig } from './types';
import { TCString } from '@iabtcf/core';
import { type IConfig } from '@players/types';
import { loadScript } from '@players/CoreAudioPlayer/utils';

const timeStamp = Date.now();
const randomNumber = Math.floor(Math.random() * 10000000 + 1);

export const loadSdk = async (): Promise<number> =>
  await new Promise((resolve, reject) => {
    const script = loadScript(JWPLAYER_SDK);
    script.async = false;
    script.onload = () => {
      try {
        if (typeof window.jwplayer === 'function') {
          resolve(0);
        }
      } catch (error) {
        reject(error);
      }
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });

const TALPA_CONSENT = {
  id: null,
  scope: null,
  name: 'talpa_consent used for ttag',
  purposes: [2, 3, 4, 7, 9],
};

function getPurposesAllowed(purposes, gdprConsent): boolean {
  if (!Array.isArray(purposes)) return false;

  const consentData = gdprConsent ? TCString.decode(gdprConsent) : null;
  if (!consentData) return false;

  return purposes.every((purpose) => {
    return consentData.purposeConsents.has(purpose);
  });
}

export const prerollConsentTag = (gdprConsent: string): number => {
  if (getPurposesAllowed(TALPA_CONSENT?.purposes, gdprConsent)) {
    return 1;
  }
  return 0;
};

export const getAdTag = (configParams: IConfig, media: ActivePodcastOrStation): string => {
  const talpaConsentValue = prerollConsentTag(configParams?.gdprConsent);
  const duration = media?.contentTypeId === StreamType.PODCAST ? media?.durationSeconds ?? 0 : 3600;
  const freewheelAdRequest = 'https://7b936.v.fwmrm.net/ad/g/1';

  const tagParameters = {
    prof: '506166:sanoma_sbs_ovp_js_live',
    nw: 506166,
    caid: 'juke_partner_player_asset',
    vdur: duration,
    vprn: timeStamp,
    pvrn: randomNumber,
    csid: configParams.csid,
    resp: 'vast3',
    crtp: 'vast3ap',
    flag: encodeURIComponent('+sltp+emcr+exvt+fbad'),
    talpa_consent: talpaConsentValue,
    _fw_gdpr: 1,
    _fw_gdpr_consent: configParams?.gdprConsent ?? '',
  };

  // Decode URI component to avoid percent encoding, except for 'flag',
  // so that we 1:1 match the previously constructed string.
  const query = Object.entries(tagParameters)
    .map(([key, value]) => (key !== 'flag' ? decodeURIComponent(`${key}=${value}`) : `${key}=${value}`))
    .join('&');

  return `${freewheelAdRequest}?${query}`;
};

export const config = (): PlayerConfig => {
  return {
    file: '/preroll.mp4', // TODO find 1x1 transparent video and use it instead current one because of performance this is TEMPORARY
    width: '300px',
    height: '100%',
    deferAds: {},
    allowFullscreen: false,
    stretching: 'uniform',
    autostart: true,
    advertising: {
      client: 'vast',
      adscheduleid: 'Fd87bE12',
      vpaidcontrols: true,
    },
  };
};

export const shouldPlayAd = (): boolean => {
  const currentTime = Date.now();
  const lastAdPlayedTime = Cookies.get('lastAdPlayedTime');
  const lastAdPlayedTimeFormatted = lastAdPlayedTime ? Number.parseInt(lastAdPlayedTime) : null;

  return Boolean(!lastAdPlayedTimeFormatted || currentTime - lastAdPlayedTimeFormatted >= 30 * 60 * 1000);
};
